import React, { FC, useEffect, useState } from 'react';
import { useRootStore } from '@src/model';
import { observer } from 'mobx-react-lite';
import { getResumeDetail } from '@service/resumeDetail';
import { useRequest } from 'ahooks';
import { SOURCE_TYPE, LX_RESUME } from '@common/const';
import { Loading } from '@ss/mtd-react';
import { LxPageView } from '@utils/siteLx';
import BaseInfoView from './baseInfoView';
import EduExperienceView from './eduExperienceView';
import WorkExperienceView from './workExperienceView';
import ProjectExperienceView from './projectExperienceView';
import HonorView from './honorView';
import LanguageStandardView from './languageStandardView';
import CertificateView from './certificateView';
import SkillStandardView from './skillStandardView';
import PaperView from './paperView';
import CompetitionView from './competitionView';
import PatentView from './patentView';
import ExamView from './examView';
import SocialAccountView from './socialAccountView';
import SelfDescriptionView from './selfDescriptionView';
import SampleReelView from './sampleReelView';
import Empty from './empty';

import './index.scss';
import CampusExperienceView from './campusExperienceView';

interface IProps {
  type: string;
  setMode: Function;
  setType: Function;
  selectTYpe: Function;
  resumeEnum: any;
}

const ResumeDetailView: FC<IProps> = (props) => {
  const { type, setMode, setType, selectTYpe, resumeEnum } = props;

  const { campusResumeDetail, socialResumeDetail } = useRootStore();
  const [loading, setLoading] = useState(true);

  // 获取简历详情 (简历详情页数据)
  const { run: runData } = useRequest((source) => getResumeDetail(source), {
    manual: true,
    onSuccess: (result) => {
      if (type === 'social') {
        socialResumeDetail.setStoreData(result);
      }
      if (type === 'campus') {
        campusResumeDetail.setStoreData(result);
      }
    },
    onFinally: () => {
      setLoading(false);
    },
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    LxPageView({ cid: LX_RESUME.resumeView, valLab: { resumeType: type } });
  }, []);

  // 初始数据获取
  useEffect(() => {
    if (resumeEnum) {
      if (type === 'social') {
        if (resumeEnum?.socialResume) runData({ source: SOURCE_TYPE.social });
        // 如果没有该类型简历，跳转到另一份简历
        else if (resumeEnum?.campusResume) {
          setType('campus');
        } else setLoading(false);
      }

      if (type === 'campus') {
        if (resumeEnum?.campusResume) runData({ source: SOURCE_TYPE.campus });
        else if (resumeEnum?.socialResume) {
          setType('social');
        } else setLoading(false);
      }
    }
  }, [resumeEnum, type]);

  let store;
  if (type === 'campus') store = campusResumeDetail;
  if (type === 'social') store = socialResumeDetail;
  const {
    resumeBaseInfo,
    resumeEduExpList,
    resumeWorkExpList,
    resumeProjectExpList,
    resumeCampusExpList,
    resumeHonorList,
    resumeLanguageList,
    resumeCertificateList,
    resumeSkillList,
    resumeThesisList,
    resumeCompetitionList,
    resumePatentList,
    resumeExamList,
    resumeSocialAccountList,
    resumeWorksAttachmentList,
    description,
  } = store || {};

  // 加载中
  if (loading) {
    return <Loading size="large" message="加载中" direction="vertical" />;
  }

  if (!resumeEnum?.campusResume && !resumeEnum?.socialResume) {
    return <Empty text="你当前还没有创建简历" buttonText="立即创建" onclick={selectTYpe} />;
  }

  return (
    <div className="resume_detail_view">
      <BaseInfoView type={type} data={resumeBaseInfo} setMode={setMode} />
      {!!resumeEduExpList?.length && <EduExperienceView type={type} data={resumeEduExpList} setMode={setMode} />}
      {!!resumeLanguageList?.length && type === 'campus' && (
        <LanguageStandardView type={type} data={resumeLanguageList} setMode={setMode} />
      )}
      {!!resumeWorkExpList?.length && <WorkExperienceView type={type} data={resumeWorkExpList} setMode={setMode} />}
      {!!resumeProjectExpList?.length && (
        <ProjectExperienceView type={type} data={resumeProjectExpList} setMode={setMode} />
      )}
      {type === 'campus' && !!resumeCampusExpList?.length && (
        <CampusExperienceView type={type} data={resumeCampusExpList} setMode={setMode} />
      )}
      {!!resumeHonorList?.length && <HonorView type={type} data={resumeHonorList} setMode={setMode} />}
      {!!resumeLanguageList?.length && type === 'social' && (
        <LanguageStandardView type={type} data={resumeLanguageList} setMode={setMode} />
      )}
      {!!resumeCertificateList?.length && (
        <CertificateView type={type} data={resumeCertificateList} setMode={setMode} />
      )}
      {type === 'campus' && !!resumeSkillList?.length && <SkillStandardView data={resumeSkillList} setMode={setMode} />}
      {type === 'campus' && !!resumeThesisList?.length && <PaperView data={resumeThesisList} setMode={setMode} />}
      {type === 'campus' && !!resumeCompetitionList?.length && (
        <CompetitionView data={resumeCompetitionList} setMode={setMode} />
      )}
      {type === 'social' && !!resumePatentList?.length && <PatentView data={resumePatentList} setMode={setMode} />}
      {type === 'social' && !!resumeExamList?.length && <ExamView data={resumeExamList} setMode={setMode} />}
      {type === 'social' && !!resumeSocialAccountList?.length && (
        <SocialAccountView data={resumeSocialAccountList} setMode={setMode} />
      )}
      {!!resumeWorksAttachmentList?.length && (
        <SampleReelView type={type} data={resumeWorksAttachmentList} setMode={setMode} />
      )}
      {type === 'social' && <SelfDescriptionView data={description} setMode={setMode} />}
    </div>
  );
};

export default observer(ResumeDetailView);
